import { action, makeObservable, observable, computed } from "mobx";
import { fabric } from "fabric";
import computeBorderedPointCoords from "pages/labelerSegmentation2/tools/computeBorderedPointCoords";
import _ from "lodash";
import RootStore from "store/rootStore";
import { pastePolygonsWithoutConfirm } from "./pastePolygonsHandler";

const SEPARATOR = "||";
const BORD_DETECT_LIMIT = 50;
export const ZOOM_MIN = 1;
export const ZOOM_MAX = 20;
export const POINTS_MIN = 3;
export const POINT_CLOSE_LIMIT = 3;
export const ENABLE_DOUBLE_CLICK_CLOSE_POLYGON = false;

class LabelerSegmentationUiStore {
  @observable scaling: any;
  @observable zoom: number = 1;
  @observable moveInZoom: boolean = false;
  @observable borderMode: boolean = false;
  @observable isSnackbarOpen: boolean = false;
  @observable messageSnackBar: string = "OK";
  @observable variantSnackBar: string = "success"; // "error" : "success"

  @observable polygons: Array<any> = [];
  @observable originalPolygons: Array<any> = [];

  @observable tagSelectedIndex: number = 0;

  @observable firstPoint: any = null;
  @observable lastPoint: any = null;

  @observable copyPolygons: boolean = false;
  @observable pastePolygons: boolean = false;
  @observable keepLabelsOnNextImage: boolean = false;

  @observable activePolygon: any = null;

  @observable openSkipDialog: boolean = false;
  @observable openAlertDialog: boolean = false;
  @observable alertDialogText: string = "";
  @observable alertDialogCallback: Function = () => {};
  @observable alertDialogCloseCallback: Function = () => {};

  @observable majDown: boolean = false;
  @observable selectMode: boolean = false;
  @observable deleteMode: boolean = false;
  @observable appendMode: boolean = false;
  @observable addMode: boolean = false;

  @observable resizeMode: boolean = false;
  @observable imageLoadError: boolean = false;

  constructor(private store: RootStore) {
    makeObservable(this);
  }

  @action calculateScaling = ({ width, height, newWidth, newHeight }: any) => {
    this.setScaling({
      x: newWidth / width,
      y: newHeight / height,
      newHeight,
      newWidth,
    });
  };

  buildLabels = (numImage: number) => {
    const images = this.store.labelerSegmentationStore.images;
    const image = images[numImage];
    return image ? image.labels.polygonLabels : [];
  };

  @action loadImage = (numImage: any) => {
    const { canvas, addImageToCanvas } = this.store.fabricSegmentationStore;

    this.setImageLoadError(false);

    if (canvas) {
      canvas.getObjects().forEach((o: any) => canvas.remove(o));
    }

    const image = this.store.labelerSegmentationStore.images[numImage];

    const url = image ? image.url : "/404.png";
    const labels: any[] = this.buildLabels(numImage);

    // this.setState({ imageLoaded: false });

    var img = new Image();

    img.onload = () => {
      fabric.Image.fromURL(img.src, () => {
        if (image) {
          const { width, height } = img;
          const { newWidth, newHeight } = this.scaling;
          this.calculateScaling({ width, height, newWidth, newHeight });

          if (this.keepLabelsOnNextImage) {
            this.loadPolygons(labels);
            pastePolygonsWithoutConfirm(this.store);
            this.setKeepLabelsOnNextImage(false);
          } else {
            this.loadPolygons(labels);
          }
        }
        setTimeout(() => addImageToCanvas(img), 50);
      });
    };

    img.onerror = () => {
      this.setImageLoadError(true);
      fabric.util.loadImage("/404.png", (img: any) => {
        setTimeout(() => addImageToCanvas(img), 50);
      });
    };

    img.src = url;
  };

  @action loadPolygons = (labels: Array<any>) => {
    const polys = labels.reduce(
      (memo: any, l: any) => ({
        ...memo,
        [l.labelValue]: memo[l.labelValue] ? memo[l.labelValue].concat(l) : [l],
      }),
      {}
    );
    const polygons = Object.keys(polys).reduce(
      (memo: any, key: string) =>
        memo.concat(
          polys[key].reduce(
            (m: any, l: any, idx: number) =>
              m.concat({
                labelValue: `${key}${SEPARATOR}${idx}`,
                done: true,
                deleted: false,
                points: l.allX.map((x: any, index: number) => ({
                  x: x * this.scaling.x,
                  y: l.allY[index] * this.scaling.y,
                })),
                visible: true,
              }),
            []
          )
        ),
      []
    );

    this.setPolygons(polygons);
    this.setOriginalPolygons(polygons);
  };

  @action polygonDoneCount = () => this.polygonCount(this.tagSelectedIndex);

  @computed get hasModifications() {
    const index = this.store.labelerSegmentationStore.numImage;
    return (
      !_.isEqual(this.polygons, this.originalPolygons) ||
      !_.isEqual(
        this.store.labelerSegmentationStore.images[index],
        this.store.labelerSegmentationStore.originalImages[index]
      )
    );
  }

  @action closeCurrentPolygon = () => {
    const polygonNotDone = this.findPolygonNotDone();
    if (polygonNotDone) {
      const polygons = this.polygons.map((p) =>
        // p.done ? p : { ...p, done: true }
        p.labelValue === polygonNotDone.labelValue ? { ...p, done: true } : p
      );
      this.setPolygons(polygons);
    }
  };

  polygonCount = (index: number) =>
    this.polygons.filter((p: any) => {
      const legend = this.store.labelerSegmentationStore.tags[index].legend;
      return p.labelValue.split(SEPARATOR)[0] === legend && p.done;
    }).length;

  @action deletePolygonByName = (name: string) => {
    this.setPolygons(
      this.polygons.filter((p) => !p.labelValue.startsWith(name))
    );
  };

  getCurrentPolygonName = () => {
    return `${
      this.store.labelerSegmentationStore.tags[this.tagSelectedIndex].legend
    }${SEPARATOR}${this.polygonDoneCount()}`;
  };

  getPolygonName = (index: number) => {
    return `${
      this.store.labelerSegmentationStore.tags[index].legend
    }${SEPARATOR}${this.polygonCount(index)}`;
  };

  findPolygonNotDone = (noPoly: any = null) =>
    this.polygons.find(
      (p) =>
        p.labelValue ===
          `${
            this.store.labelerSegmentationStore.tags[this.tagSelectedIndex]
              .legend
          }${SEPARATOR}${noPoly || this.polygonDoneCount()}` &&
        !p.done &&
        !p.deleted
    );

  areAllPolygonsDone = () => this.polygons.filter((p) => !p.done).length === 0;

  canSave = () => {
    const images = this.store.labelerSegmentationStore.images;
    const numImage = this.store.labelerSegmentationStore.numImage;
    return (
      (this.polygons.length > 0 &&
        this.polygons.filter((p) => p.points.length < POINTS_MIN).length ===
          0) ||
      images[Number(numImage)]?.motif
    );
  };

  @computed get isZoomIn() {
    return this.zoom > 1;
  }

  @computed get isZoomOut() {
    return this.zoom < 1;
  }

  getSavedPolygons = () => {
    let savedPolygonsString = window.localStorage.getItem(`saved_polygons`);
    return JSON.parse(savedPolygonsString ?? "null");
  };

  setSavedPolygons = (savedPolygons: any) => {
    window.localStorage.setItem(
      `saved_polygons`,
      JSON.stringify(savedPolygons)
    );
  };

  hasSavedPolygons = () => {
    const { numImage, batch } = this.store.labelerSegmentationStore;

    const savedPolygons = this.getSavedPolygons();
    return savedPolygons && savedPolygons.polygons;
    // && (savedPolygons.batchId !== batch.id || savedPolygons.numImage !== numImage)
  };

  @action updatePolygonPoints = (polygon: any, points: Array<any>) => {
    const { deleteSegmentByName } = this.store.labelerSegmentationStore;
    const { stickMode, resetPolyStick } = this.store.stickTwoPolygonsStore;

    // Delete polygon if there is 1 point or less
    // (if only one point we cannot delete the polygon because we do not see it)
    if (points.length <= 1) {
      deleteSegmentByName(polygon.labelValue);
      return;
    }

    if (stickMode) {
      resetPolyStick();
    }

    this.updatePolygon(polygon, { points });
  };

  @action updatePolygon = (polygon: any, obj: any) => {
    const newPolygons = this.polygons.map((p) =>
      p.labelValue === polygon.labelValue ? { ...p, ...obj } : p
    );
    this.setPolygons(newPolygons);
  };

  @action createPolygon = (points: Array<any>) => {
    const labelValue = this.createLabelValue();
    const newPolygon = {
      labelValue,
      points: points.map((point) => this.calculatePoint(point)),
      done: false,
      deleted: false,
      visible: true,
    };
    this.setPolygons(this.polygons.concat(newPolygon));
  };

  createLabelValue = (index: number = this.tagSelectedIndex) => {
    const { tags } = this.store.labelerSegmentationStore;
    const numberPolygonDone = this.polygonDoneCount();
    return `${tags[index].legend}${SEPARATOR}${numberPolygonDone}`;
  };

  @action addPoint = (polygon: any, point: any) => {
    const newPoint = this.calculatePoint(point);
    const newPoints = polygon.points.concat(newPoint);
    this.updatePolygonPoints(polygon, newPoints);
  };

  calculatePoint = (point: any) => {
    let { x, y } = point;
    const {
      fabricSegmentationStore: { borderModeIndicator },
      labelerSegmentationUiStore: { scaling, borderMode },
    } = this.store;

    if (borderMode) {
      const borderSide = borderModeIndicator
        ? borderModeIndicator.get("borderSide")
        : null;
      const coords = computeBorderedPointCoords(
        { x, y },
        scaling,
        BORD_DETECT_LIMIT,
        borderSide
      );
      x = coords.x;
      y = coords.y;
    }

    return { x, y };
  };

  @action toggleAllVisibility = (visibility: boolean) => {
    this.setPolygons(
      this.polygons.map((polygon) => ({ ...polygon, visible: visibility }))
    );
  };

  @action toggleVisibility = (name: string, visibility: boolean) => {
    this.setPolygons(
      this.polygons.map((polygon) => {
        return polygon.labelValue.startsWith(name)
          ? { ...polygon, visible: visibility }
          : polygon;
      })
    );
  };

  //// SETTERS /////////////////////////////////////////////////
  //////////////////////////////////////////////////////////////

  @action setScaling = (scaling: any) => {
    this.scaling = scaling;
  };

  @action setPolygons = (polygons: Array<any>) => {
    this.polygons = polygons;
  };

  @action setOriginalPolygons = (originalPolygons: Array<any>) => {
    this.originalPolygons = originalPolygons;
  };

  @action setTagSelectedIndex = (tagSelectedIndex: number) => {
    this.tagSelectedIndex = tagSelectedIndex;
  };

  @action setSelectMode = (selectMode: boolean) => {
    this.selectMode = selectMode;
  };

  @action setLastPoint = (lastPoint: any) => {
    this.lastPoint = lastPoint;
  };

  @action setFirstPoint = (firstPoint: any) => {
    this.firstPoint = firstPoint;
  };

  @action setActivePolygon = (activePolygon: any) => {
    this.activePolygon = activePolygon;
  };

  @action setZoom = (zoom: number) => {
    this.zoom = zoom;
  };

  @action setMoveInZoom = (moveInZoom: boolean) => {
    this.moveInZoom = moveInZoom;
  };

  @action setBorderMode = (borderMode: boolean) => {
    this.borderMode = borderMode;
  };

  @action setDeleteMode = (deleteMode: boolean) => {
    this.deleteMode = deleteMode;
  };

  @action setAddMode = (addMode: boolean) => {
    this.addMode = addMode;
  };

  @action setCopyPolygons = (copyPolygons: boolean) => {
    this.copyPolygons = copyPolygons;
  };

  @action setPastePolygons = (pastePolygons: boolean) => {
    this.pastePolygons = pastePolygons;
  };

  @action setKeepLabelsOnNextImage = (keepLabelsOnNextImage: boolean) => {
    this.keepLabelsOnNextImage = keepLabelsOnNextImage;
  };

  @action setOpenSkipDialog = (openSkipDialog: boolean) => {
    this.openSkipDialog = openSkipDialog;
  };

  @action setOpenAlertDialog = (openAlertDialog: boolean) => {
    this.openAlertDialog = openAlertDialog;
  };

  @action setAlertDialogText = (alertDialogText: string) => {
    this.alertDialogText = alertDialogText;
  };

  @action setAlertDialogCallback = (alertDialogCallback: Function) => {
    this.alertDialogCallback = alertDialogCallback;
  };

  @action setAlertDialogCloseCallback = (
    alertDialogCloseCallback: Function
  ) => {
    this.alertDialogCloseCallback = alertDialogCloseCallback;
  };

  @action setIsSnackbarOpen = (isSnackbarOpen: boolean) => {
    this.isSnackbarOpen = isSnackbarOpen;
  };

  @action setMessageSnackBar = (messageSnackBar: string) => {
    this.messageSnackBar = messageSnackBar;
  };

  @action setVariantSnackBar = (variantSnackBar: string) => {
    this.variantSnackBar = variantSnackBar;
  };

  @action setResizeMode = (resizeMode: boolean) => {
    this.setAddMode(false);
    this.setDeleteMode(false);
    const { canvas, drawPolygon } = this.store.fabricSegmentationStore;
    const { polygons } = this.store.labelerSegmentationUiStore;

    this.resizeMode = resizeMode;
    canvas.renderAll();
    polygons.forEach((p: any) => drawPolygon(p));
  };

  @action setImageLoadError = (imageLoadError: boolean) => {
    this.imageLoadError = imageLoadError;
  };
}

export default LabelerSegmentationUiStore;
